import i18n from "@/utils/i18n";

/** Backend status: 2=IN_REVIEW, 3=RESOLVED (artık sadece bu iki durum) */
export const SupportTicketStatus = {
  IN_REVIEW: 2,
  RESOLVED: 3,
} as const;

export type TicketStatusBackend = 2 | 3;

/** Admin ve client arayüzünde aynı durum metinleri (i18n) */
export const SUPPORT_TICKET_STATUS_LABELS_TR: Record<
  TicketStatusBackend,
  string
> = new Proxy({} as Record<TicketStatusBackend, string>, {
  get(_target, prop) {
    if (prop === "2") return i18n.t("supportTicketStatus.inReview");
    if (prop === "3") return i18n.t("supportTicketStatus.resolved");
    return "";
  },
});

export const SUPPORT_TICKET_STATUS_CHIP_TYPE: Record<
  TicketStatusBackend,
  "info" | "success" | "error"
> = {
  2: "info",
  3: "success",
};

/** Backend sıralama - GET /api/admin/support-tickets sortBy */
export const SupportTicketSortBy = {
  /** Önce Cevaplananlar */
  ANSWERED_FIRST: "answeredFirst",
  /** Önce Bekleyenler */
  PENDING_FIRST: "pendingFirst",
  /** Talep Tarihi (en yeni) */
  CREATED_AT_DESC: "createdAtDesc",
  /** Talep Tarihi (en eski) */
  CREATED_AT_ASC: "createdAtAsc",
} as const;

export type SupportTicketSortByValue =
  (typeof SupportTicketSortBy)[keyof typeof SupportTicketSortBy];

/** GET /api/admin/support-tickets - query params */
export interface SupportTicketListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: TicketStatusBackend;
  /** Sadece bu eğitime bağlı talepleri getir (UUID). */
  educationId?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Sıralama: answeredFirst | pendingFirst | createdAtDesc | createdAtAsc */
  sortBy?: SupportTicketSortByValue;
}

/** Talep eğitime bağlıysa eğitim özeti; değilse null */
export interface SupportTicketEducationRef {
  id: string;
  name: string;
}

/** GET /api/admin/support-tickets - single list item */
export interface SupportTicketListItem {
  id: string;
  requestNo: string;
  personnelName: string;
  createdAt: string;
  status: TicketStatusBackend;
  /** Talep bir eğitime bağlıysa eğitim bilgisi, değilse null. */
  education?: SupportTicketEducationRef | null;
  /** true => son mesaj talep sahibinden (personel yanıtı bekleniyor / "Bekleyen"). */
  awaitingStaffReply?: boolean;
  /** Son mesajın zamanı (son aktivite). Mesaj yoksa null. */
  lastMessageAt?: string | null;
}

/** GET /api/admin/support-tickets - response (backend: items veya data) */
export interface SupportTicketListResponse {
  items?: SupportTicketListItem[];
  data?: SupportTicketListItem[];
  count: number;
}

/** GET /api/admin/support-tickets/{id} - sender (gönderen bilgisi) */
export interface SupportTicketSender {
  id: string;
  firstName: string;
  lastName: string;
  departmentName: string;
  title: string;
  email: string;
  phoneNumber: string;
}

/** GET /api/admin/support-tickets/{id} - data.files[] */
export interface SupportTicketFile {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  /** İndirme için kullanılacak URL (relative veya absolute) */
  downloadUrl?: string;
}

/** Karşılıklı konuşmadaki tek bir mesaj. isStaff=true => destek personeli, false => talep sahibi. */
export interface SupportTicketThreadMessage {
  id: string;
  content: string | null;
  file: SupportTicketFile | null;
  senderName: string;
  /** Mesajı destek personeli mi yazdı? true=personel, false=talep sahibi. */
  isStaff: boolean;
  createdAt: string;
}

/** GET /api/admin/support-tickets/{id} - API response data */
export interface SupportTicketDetailResponse {
  id: string;
  requestNo: string;
  title: string;
  description: string;
  status: TicketStatusBackend;
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string | null;
  /** Talep bir eğitime bağlıysa eğitim bilgisi, değilse null. */
  education?: SupportTicketEducationRef | null;
  sender: SupportTicketSender;
  /** Tüm mesajlardaki eklerin listesi (downloadUrl ile) */
  files: SupportTicketFile[];
  /** Karşılıklı konuşmanın tamamı (eski->yeni). isStaff ile taraf ayrımı yapılır. */
  messages: SupportTicketThreadMessage[];
}

/** Backend status 2|3 → frontend TicketStatus (eski string etiketler) */
export function mapTicketStatusBackendToFrontend(
  status: TicketStatusBackend,
): TicketStatus {
  const map: Record<TicketStatusBackend, TicketStatus> = {
    2: "inceleniyor",
    3: "cozuldu",
  };
  return map[status] ?? "inceleniyor";
}

export type TicketImportance = "dusuk" | "orta" | "yuksek";
export type TicketStatus =
  | "olusturuldu"
  | "inceleniyor"
  | "cozuldu"
  | "reddedildi";

export interface SupportTicket {
  id: string;
  ticketNo: string;
  personnelName: string;
  ticketType: string;
  importance: TicketImportance;
  createdAt: string;
  status: TicketStatus;
  subject?: string;
  description?: string;
}

export interface SupportTicketDetail extends SupportTicket {
  subject: string;
  personnel: {
    name: string;
    department: string;
    title: string;
    email: string;
    phone: string;
  };
  internalNotes: InternalNote[];
  messages: TicketMessage[];
  attachments?: TicketAttachment[];
}

export interface InternalNote {
  id: string;
  authorName: string;
  createdAt: string;
  content: string;
}

export interface TicketMessage {
  id: string;
  senderName: string;
  createdAt: string;
  content: string;
  isStaff?: boolean;
}

export interface TicketAttachment {
  id: string;
  fileName: string;
  size: string;
}

export interface SupportTicketFilters {
  search?: string;
  status?: TicketStatus;
  ticketType?: string;
}

/** GET /api/admin/support-tickets/summary - response data */
export interface SupportTicketSummary {
  totalCount: number;
  pendingCount: number;
  resolvedCount: number;
}
